import React, { useEffect, useMemo, useRef, useState } from 'react';

import { usePermissions } from '../context/PermissionContext';
import {
  ADMISSION_PERMISSIONS, hasOpenDeathRecord, MINE_EMPTY_TITLE, MINE_EMPTY_BODY, MINE_SEE_ALL_COPY,
} from '../constants/admission';
import { listAdmissions } from '../services/api/admissionApi';
import { getPatient } from '../services/api/visitApi';
import { matchSuggestions, treatingDoctorLabel } from '../components/admission/deskFilters';
import ViewerRelationshipChip, { VIEWER_RELATIONSHIP } from '../components/admission/ViewerRelationshipChip';
// `describeAttender` — a pure, stateless selector with NO diagnostics/lab
// knowledge (name/relation/mobile only). Explicitly sanctioned reuse
// (build-plan finding: "reusing the shared describeAttender the card
// already uses") — the framework never imports DiagnosticsCard,
// OrderLabDialog or any lab API helper.
import { describeAttender } from '../components/admission/diagnostics/diagnosticsModel';
import { formatDateIST } from '../utils/formatIST';
import { patientAgeDisplay } from '../utils/ageValue';
import AdminPageHeader from '../components/common/AdminPageHeader';
import ScopeCaption from '../components/common/ScopeCaption';
import DeptAccessBlockedPanel from '../components/admission/DeptAccessBlockedPanel';
import { useDeptAccessBlocked } from '../components/admission/hooks';
import DiagnosticsView from '../components/ipd/DiagnosticsView';

const PAGE_SIZE = 10;

// admission-access M-FE-2 (ADD-1, S17) — a treating-capable viewer
// (`viewer_can_be_treating`) lands on "My admissions": the rows the SERVER says
// they are the primary or an added doctor on (`viewer_relationship`). A client
// filter over the rows already fetched — never a second request. Copy:
// copy-ratified #33/#34; the "All" / "See all currently admitted" labels are
// from mockup 6 (PO to ratify).
const SCOPE_MINE = 'mine';
const SCOPE_ALL = 'all';
const ROLE_ALL = 'all';

function wardBedLine(row) {
  return [row.ward_name, row.bed_label ? `Bed ${row.bed_label}` : null].filter(Boolean).join(', ') || null;
}

/**
 * DeceasedChip — the death signal, identical in wording and tone to the one
 * `AdmissionRailItem` and `AdmissionWorkspace` render on `/org/admissions`.
 *
 * It reads the SHARED `hasOpenDeathRecord` predicate, never `row.death_recorded`
 * directly: this page receives the picker projection (a `death_recorded` bool)
 * while the desk receives the full `cause_of_death` map, and the ONE predicate
 * is what keeps the two pages agreeing about who is deceased.
 *
 * Why it exists: the death-record slice shipped this signal on the desk rail,
 * the workspace banner and the bed board, but not here — so a death recorded at
 * 02:00 was invisible to anyone working the IPD page.
 */
function DeceasedChip({ admission, testId }) {
  if (!hasOpenDeathRecord(admission)) return null;
  return (
    <span
      className="inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-bold border-red-400 bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200"
      data-testid={testId}
    >
      ● Deceased · Pending discharge
    </span>
  );
}

/** One pill of the My admissions / All admitted switch and the Primary / Added narrowing. */
function ScopeChip({ on, onClick, count, testId, small = false, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      data-testid={testId}
      className={`rounded-full border font-semibold ${small ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-[12px]'} ${on
        ? 'border-teal-600 bg-teal-600 text-white'
        : 'border-slate-400 dark:border-slate-500 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
    >
      {children} <span className="tabular-nums opacity-80">{count}</span>
    </button>
  );
}

/**
 * IpdPage (C6a/D30) — the IPD framework: the `/org/ipd` route, the
 * `ACCESS_IPD`-or-`VIEW_CLINICAL_RECORD` gate, finding the admitted patient,
 * and the read-only patient-details block. IDENTICAL for every role, forever.
 *
 * ⚠ THE HARD RULE (D28) — stripped at the DATA level. This page calls
 * ONLY the money-free picker projection (`listAdmissions({ view: 'picker' })`,
 * D-2 frozen contract) — never `getRunningBill`, never `getAdmissionEvents`,
 * never a deposit/dues/discharge function. Selecting a patient reads the row
 * already on the picker list — ZERO extra requests.
 *
 * The framework knows NOTHING about diagnostics — it renders exactly one
 * child, `DiagnosticsView`, and passes it exactly one prop: `admission`. That
 * single prop is what makes the framework/child split real, not cosmetic
 * (D30) — a second role's view drops in beside it later without touching
 * this file.
 *
 * ⚠ FORBIDDEN, and tested: no role literal, no role branch, no registry, no
 * `viewType` prop, no config table.
 */
export default function IpdPage() {
  const { hasPermission } = usePermissions();
  // ADD-1 (g247) — ACCESS_IPD OR VIEW_CLINICAL_RECORD (ACCESS_IPD not widened).
  const canAccess = hasPermission(ADMISSION_PERMISSIONS.ACCESS_IPD)
    || hasPermission(ADMISSION_PERMISSIONS.VIEW_CLINICAL_RECORD);

  const [rows, setRows] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  // ADD-1 — the server's word that the caller can be a treating doctor (drives
  // the My admissions / All admitted switch with NO role check on this page).
  const [viewerCanBeTreating, setViewerCanBeTreating] = useState(false);
  // S13 — the picker envelope's `dept_scoped` (the server's word that the admission
  // wall applied to this viewer; strict-true, absent = not walled).
  const [deptScoped, setDeptScoped] = useState(false);
  const [serverBlocked, setServerBlocked] = useState(null); // null = server hasn't said
  const deptAccessBlocked = useDeptAccessBlocked(serverBlocked);
  const [scopePick, setScopePick] = useState(null); // null = the default for this viewer
  const [roleFilter, setRoleFilter] = useState(ROLE_ALL);

  // ONE bounded fetch, single-flight (fires once per mount, never again on
  // re-render — `loadedRef` guards a StrictMode double-invoke too).
  const loadedRef = useRef(false);
  useEffect(() => {
    if (!canAccess || loadedRef.current) return;
    loadedRef.current = true;
    // bk-695 — no `status`: the server resolves "currently admitted" from
    // its own CURRENTLY_ADMITTED_STATUSES (admission_state.py), the SAME
    // four-status set Manage Admission's desk bucket uses, INCLUDING
    // mid-discharge (DISCHARGE_INITIATED / BILLING_CLEARANCE). This page
    // must never enumerate statuses itself again.
    listAdmissions({ view: 'picker' }).then((res) => {
      if (!res.success) { setRows([]); setError(res.error || 'Failed to load admitted patients.'); return; }
      setViewerCanBeTreating(res.data.viewer_can_be_treating === true);
      setDeptScoped(res.data.dept_scoped === true);
      setServerBlocked(typeof res.data.dept_access_blocked === 'boolean' ? res.data.dept_access_blocked : null);
      setRows(res.data.admissions || []);
    });
  }, [canAccess]);

  // My admissions = the rows where the server says the viewer is on the care
  // team. Derived from the one fetched list; counts come from the same pass.
  const mineRows = useMemo(() => (rows || []).filter((r) => r.viewer_relationship), [rows]);
  const mineCounts = useMemo(() => ({
    [ROLE_ALL]: mineRows.length,
    [VIEWER_RELATIONSHIP.PRIMARY]: mineRows.filter((r) => r.viewer_relationship === VIEWER_RELATIONSHIP.PRIMARY).length,
    [VIEWER_RELATIONSHIP.ADDED]: mineRows.filter((r) => r.viewer_relationship === VIEWER_RELATIONSHIP.ADDED).length,
  }), [mineRows]);
  const scope = scopePick || (viewerCanBeTreating ? SCOPE_MINE : SCOPE_ALL);
  const scopedRows = useMemo(() => {
    if (scope !== SCOPE_MINE) return rows || [];
    return roleFilter === ROLE_ALL ? mineRows : mineRows.filter((r) => r.viewer_relationship === roleFilter);
  }, [rows, scope, mineRows, roleFilter]);
  const pickScope = (next) => { setScopePick(next); setRoleFilter(ROLE_ALL); setExpanded(false); };
  const pickRole = (next) => { setRoleFilter(next); setExpanded(false); };

  const trimmed = q.trim();
  const visibleRows = useMemo(() => {
    if (!trimmed) return expanded ? scopedRows : scopedRows.slice(0, PAGE_SIZE);
    // Reuse the desk's existing matcher — never a second one — capped at the
    // full scoped set so every match shows while a query narrows the rail.
    return matchSuggestions(scopedRows, trimmed, scopedRows.length).items;
  }, [scopedRows, trimmed, expanded]);

  const showMore = !trimmed && scopedRows.length > PAGE_SIZE && !expanded;
  const mineEmpty = scope === SCOPE_MINE && mineRows.length === 0;

  // The details block reads the row already on the picker list — no lookup,
  // no second fetch. `age` is the ONE exception (AGE-FE, `AGE-EDIT-spec.md`
  // §4): unlike mobile/address/allergies, age is NOT denormalized onto the
  // admission row (no admission-side copy exists, §3.1 — nothing to add
  // here without inventing a new stale class), so it is read live, but
  // ONLY for the SELECTED admission — never the list. Same pattern
  // `AdmissionWorkspace.jsx`'s Details-tab `patientAddress` fetch already
  // uses: one `getPatient` per selection change (a stable primitive dep,
  // `selected.patient_id`), never per-row, never on list render.
  const selected = useMemo(
    () => (rows || []).find((r) => r.admission_id === selectedId) || null,
    [rows, selectedId],
  );
  const selectedPatientId = selected ? selected.patient_id : null;
  const [selectedAge, setSelectedAge] = useState(undefined);
  useEffect(() => {
    let cancelled = false;
    setSelectedAge(undefined);
    if (selectedPatientId) {
      getPatient(selectedPatientId)
        .then((p) => { if (!cancelled) setSelectedAge(patientAgeDisplay(p) || null); })
        .catch(() => { if (!cancelled) setSelectedAge(null); });
    }
    return () => { cancelled = true; };
  }, [selectedPatientId]);

  if (!canAccess) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        <AdminPageHeader />
        <div className="mx-auto max-w-3xl p-6">
          <div role="alert" data-testid="no-access" className="rounded-xl border border-amber-300 bg-amber-50 p-5 text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-100">
            <h2 className="mb-1 text-lg font-semibold">IPD</h2>
            <p className="text-sm">You do not have access to the IPD page. Contact your administrator for access.</p>
          </div>
        </div>
      </div>
    );
  }

  if (deptAccessBlocked) return <DeptAccessBlockedPanel />;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="ipd-page">
      <AdminPageHeader />
      <div className="mx-auto max-w-6xl p-4 md:p-6 grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4 items-start">
        <div className="rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 p-3 md:sticky md:top-4" data-testid="ipd-rail">
          <div className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-2">
            Currently admitted{rows ? ` · ${rows.length}` : ''}
          </div>
          <ScopeCaption scoped={deptScoped} className="-mt-1 mb-2" />

          {rows === null ? (
            <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="ipd-rail-loading">Loading admitted patients…</p>
          ) : error ? (
            <div className="text-[12.5px] text-red-700 dark:text-red-300" data-testid="ipd-rail-error">{error}</div>
          ) : rows.length === 0 ? (
            <div className="text-center py-4 text-[12.5px] text-slate-500 dark:text-slate-400" data-testid="ipd-rail-empty">
              🛏 No patients are admitted right now.
            </div>
          ) : (
            <>
              {viewerCanBeTreating && (
                <div className="flex gap-1.5 mb-2" data-testid="ipd-scope-switch">
                  <ScopeChip on={scope === SCOPE_MINE} onClick={() => pickScope(SCOPE_MINE)} count={mineRows.length} testId="ipd-scope-mine">My admissions</ScopeChip>
                  <ScopeChip on={scope === SCOPE_ALL} onClick={() => pickScope(SCOPE_ALL)} count={rows.length} testId="ipd-scope-all">All admitted</ScopeChip>
                </div>
              )}
              {!mineEmpty && (
                <input
                  type="text"
                  className="w-full mb-2 px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-[12.5px]"
                  placeholder="Search name / IP number / bed…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  data-testid="ipd-rail-search"
                />
              )}
              {scope === SCOPE_MINE && !mineEmpty && (
                <div className="flex gap-1.5 mb-2" data-testid="ipd-role-filter">
                  <ScopeChip small on={roleFilter === ROLE_ALL} onClick={() => pickRole(ROLE_ALL)} count={mineCounts[ROLE_ALL]} testId="ipd-role-all">All</ScopeChip>
                  <ScopeChip small on={roleFilter === VIEWER_RELATIONSHIP.PRIMARY} onClick={() => pickRole(VIEWER_RELATIONSHIP.PRIMARY)} count={mineCounts[VIEWER_RELATIONSHIP.PRIMARY]} testId="ipd-role-primary">Primary</ScopeChip>
                  <ScopeChip small on={roleFilter === VIEWER_RELATIONSHIP.ADDED} onClick={() => pickRole(VIEWER_RELATIONSHIP.ADDED)} count={mineCounts[VIEWER_RELATIONSHIP.ADDED]} testId="ipd-role-added">Added</ScopeChip>
                </div>
              )}
              {mineEmpty ? (
                <div className="text-center py-3 text-[12.5px] text-slate-500 dark:text-slate-400" data-testid="ipd-mine-empty">
                  <p className="m-0 mb-1 font-bold text-slate-700 dark:text-slate-200">{MINE_EMPTY_TITLE}</p>
                  <p className="m-0 mb-2">{MINE_EMPTY_BODY}</p>
                  <button
                    type="button"
                    onClick={() => pickScope(SCOPE_ALL)}
                    className="text-[12.5px] font-semibold text-teal-700 dark:text-teal-300 underline"
                    data-testid="ipd-mine-see-all"
                  >{MINE_SEE_ALL_COPY}</button>
                </div>
              ) : visibleRows.length === 0 ? (
                <div className="text-[12.5px] text-slate-500 dark:text-slate-400 text-center py-3" data-testid="ipd-rail-no-match">{`No patient matches "${trimmed}".`}</div>
              ) : (
                visibleRows.map((row) => (
                  <button
                    type="button"
                    key={row.admission_id}
                    onClick={() => setSelectedId(row.admission_id)}
                    data-testid={`ipd-rail-row-${row.admission_id}`}
                    className={`w-full text-left mb-1.5 px-2.5 py-2 rounded-lg border ${row.admission_id === selectedId ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/30' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] font-bold text-slate-800 dark:text-slate-100">{row.patient_name}</span>
                      <span className="ml-auto">
                        <ViewerRelationshipChip relationship={row.viewer_relationship} testId={`ipd-rail-tag-${row.admission_id}`} />
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400">
                      {[row.ip_number, wardBedLine(row)].filter(Boolean).join(' · ')}
                    </div>
                    {/* S16-AC7 — the care team, first three + "+n". Only when
                        someone was ADDED: a plain admission keeps today's row. */}
                    {Array.isArray(row.added_doctors) && row.added_doctors.length > 0 && (
                      <div className="text-[11px] text-slate-500 dark:text-slate-400 break-words" data-testid={`ipd-rail-doctors-${row.admission_id}`}>
                        {treatingDoctorLabel(row)}
                      </div>
                    )}
                    <DeceasedChip admission={row} testId={`ipd-rail-deceased-${row.admission_id}`} />
                  </button>
                ))
              )}
              {showMore && (
                <button
                  type="button"
                  onClick={() => setExpanded(true)}
                  className="w-full mt-1 py-2 rounded-lg border border-dashed border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-[12px] font-semibold"
                  data-testid="ipd-rail-show-more"
                >{`Show ${scopedRows.length - PAGE_SIZE} more`}</button>
              )}
            </>
          )}
        </div>

        <div>
          {rows !== null && rows.length === 0 ? (
            <div className="text-center py-12 px-6 rounded-2xl bg-teal-50 dark:bg-teal-900/20 border border-teal-300 dark:border-teal-700" data-testid="ipd-empty-org">
              <div className="text-2xl mb-2">🛏</div>
              <div className="text-[15px] font-extrabold text-teal-800 dark:text-teal-200 mb-1">Nobody is admitted at the moment</div>
              <div className="text-[13px] text-teal-900/80 dark:text-teal-100/80 max-w-md mx-auto">Once someone is admitted, they will appear in the list on the left.</div>
            </div>
          ) : !selected ? (
            <div className="text-center py-12 px-6 rounded-2xl bg-teal-50 dark:bg-teal-900/20 border border-teal-300 dark:border-teal-700" data-testid="ipd-nothing-picked">
              <div className="text-2xl mb-2">🏨</div>
              <div className="text-[15px] font-extrabold text-teal-800 dark:text-teal-200 mb-1">Pick a patient to see their IPD details</div>
              <div className="text-[13px] text-teal-900/80 dark:text-teal-100/80 max-w-md mx-auto">Search by name, IP number or bed on the left, or scan the list{scope === SCOPE_MINE ? '.' : <> — it&rsquo;s every currently-admitted patient.</>}</div>
            </div>
          ) : (
            <>
              <div className="rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 p-4 mb-3" data-testid="ipd-patient-details">
                <div className="flex items-center flex-wrap gap-2 mb-2.5">
                  <div className="text-[16px] font-extrabold text-slate-900 dark:text-white">{selected.patient_name}</div>
                  <DeceasedChip admission={selected} testId="ipd-details-deceased" />
                  <ViewerRelationshipChip relationship={selected.viewer_relationship} you testId="ipd-details-viewer-chip" />
                  {selected.is_mlc && (
                    <span
                      className="inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-bold border-amber-400 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200"
                      data-testid="ipd-mlc-chip"
                    >
                      MLC
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
                  <div>
                    <div className="text-[10.5px] font-extrabold uppercase tracking-wide text-slate-400 dark:text-slate-500">IP Number</div>
                    <div className="text-slate-700 dark:text-slate-200 font-semibold">{selected.ip_number}</div>
                  </div>
                  <div>
                    <div className="text-[10.5px] font-extrabold uppercase tracking-wide text-slate-400 dark:text-slate-500">Ward / Bed</div>
                    <div className="text-slate-700 dark:text-slate-200 font-semibold">{wardBedLine(selected) || '—'}</div>
                  </div>
                  <div>
                    <div className="text-[10.5px] font-extrabold uppercase tracking-wide text-slate-400 dark:text-slate-500">Primary Doctor</div>
                    <div className="text-slate-700 dark:text-slate-200 font-semibold">{selected.primary_doctor_name || '—'}</div>
                  </div>
                  <div>
                    <div className="text-[10.5px] font-extrabold uppercase tracking-wide text-slate-400 dark:text-slate-500">Admitted</div>
                    <div className="text-slate-700 dark:text-slate-200 font-semibold">{formatDateIST(selected.admitted_at) || '—'}</div>
                  </div>
                  {/* AGE-FE — the ONE field on this block that is NOT read off
                      the row (own JSDoc above, `selectedAge`): live `getPatient`
                      for the selected admission only. A blank/not-yet-loaded
                      age simply omits the row, never a fake dash. */}
                  {selectedAge && (
                    <div data-testid="ipd-patient-age">
                      <div className="text-[10.5px] font-extrabold uppercase tracking-wide text-slate-400 dark:text-slate-500">Age</div>
                      <div className="text-slate-700 dark:text-slate-200 font-semibold">{selectedAge}</div>
                    </div>
                  )}
                  {/* patient_mobile/patient_address/patient_allergies are
                      denormalized onto the admission row at admit time (see
                      AdmissionPickerView) — this page stays architecturally
                      forbidden from making a per-patient fetch FOR THESE
                      FIELDS, so they come off the row it already has, never a
                      new request. `age` (above) is the one owner-approved
                      exception (AGE-FE) because no admission-side copy of it
                      exists to read (§3.1 of the spec — adding one would be a
                      new denorm/stale class for no reason). The admissions
                      desk's Patient Details dialog is the editor for all of
                      these; here they are read-only. */}
                  {selected.patient_mobile && (
                    <div>
                      <div className="text-[10.5px] font-extrabold uppercase tracking-wide text-slate-400 dark:text-slate-500">Mobile</div>
                      <div className="text-slate-700 dark:text-slate-200 font-semibold">{selected.patient_mobile}</div>
                    </div>
                  )}
                  {selected.patient_address && (
                    <div>
                      <div className="text-[10.5px] font-extrabold uppercase tracking-wide text-slate-400 dark:text-slate-500">Address</div>
                      {/* Server copy is "line1\nline2" (structured address, formatted at admit / on Patient Details save). */}
                      <div className="text-slate-700 dark:text-slate-200 font-semibold whitespace-pre-line" data-testid="ipd-patient-address">{selected.patient_address}</div>
                    </div>
                  )}
                  {selected.patient_allergies && (
                    <div data-testid="ipd-patient-allergies">
                      <div className="text-[10.5px] font-extrabold uppercase tracking-wide text-slate-400 dark:text-slate-500">Allergies</div>
                      <div className="text-slate-700 dark:text-slate-200 font-semibold">{selected.patient_allergies}</div>
                    </div>
                  )}
                </div>

                {(() => {
                  const attender = describeAttender(selected.attender);
                  return (
                    <div className="mt-3 px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700" data-testid="ipd-attender">
                      <div className="text-[10.5px] font-extrabold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-1">Attender</div>
                      {attender.present ? (
                        <>
                          <div className="text-[13px] text-slate-700 dark:text-slate-200">
                            <b className="text-slate-900 dark:text-white">{attender.name}</b>{attender.relation ? ` (${attender.relation})` : ''}{attender.mobile ? ` · ${attender.mobile}` : ''}
                          </div>
                          {!attender.mobile && (
                            <div className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-0.5" data-testid="ipd-attender-no-phone">No phone number on file.</div>
                          )}
                        </>
                      ) : (
                        <div className="text-[13px] italic text-slate-400 dark:text-slate-500" data-testid="ipd-attender-empty">No attender recorded</div>
                      )}
                    </div>
                  );
                })()}
              </div>

              <DiagnosticsView admission={selected} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
